import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, HandHeart, FileText, Users, MessageSquare, PenSquare } from "lucide-react";

const TABS = [
  { k: "overview", l: "Overview", Icon: LayoutDashboard },
  { k: "donations", l: "Donations", Icon: HandHeart },
  { k: "grants", l: "Grants", Icon: FileText },
  { k: "volunteers", l: "Volunteers", Icon: Users },
  { k: "contacts", l: "Messages", Icon: MessageSquare },
  { k: "blog", l: "Stories", Icon: PenSquare },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ donations: [], grants: [], volunteers: [], contacts: [], blog: [] });

  const load = async () => {
    try {
      const [s, d, g, v, c, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/donations"),
        api.get("/admin/grants"),
        api.get("/admin/volunteers"),
        api.get("/admin/contacts"),
        api.get("/blog"),
      ]);
      setStats(s.data);
      setData({ donations: d.data, grants: g.data, volunteers: v.data, contacts: c.data, blog: b.data });
    } catch (e) {
      toast.error("Failed to load dashboard data");
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0C] text-white flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-white/10 bg-[#0A0A0C] sticky top-0 h-screen">
        <Link to="/" className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center bg-[#E85D04] text-white font-serif-display text-base leading-none">JD</span>
          <div className="leading-tight">
            <div className="font-serif-display text-base">John Depp</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-white/40">Admin</div>
          </div>
        </Link>
        <nav className="flex-1 py-4">
          {TABS.map(({ k, l, Icon }) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`w-full text-left px-6 py-3 text-xs tracking-[0.22em] uppercase flex items-center gap-3 transition-colors ${
                tab === k ? "bg-[#E85D04]/10 text-[#E85D04] border-l-2 border-[#E85D04]" : "text-white/60 hover:text-white"
              }`}
            >
              <Icon size={14} /> {l}
            </button>
          ))}
        </nav>
        <div className="p-6 border-t border-white/10 text-xs text-white/40 space-y-3">
          <div>{user?.email}</div>
          <button onClick={handleLogout} data-testid="admin-logout" className="text-white/60 hover:text-[#E85D04] inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase">
            <LogOut size={12} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile tabs */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0C] border-t border-white/10 flex overflow-x-auto">
        {TABS.map(({ k, l, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`mobile-admin-tab-${k}`}
            className={`flex-1 min-w-[80px] py-3 flex flex-col items-center gap-1 text-[9px] tracking-[0.18em] uppercase ${
              tab === k ? "text-[#E85D04]" : "text-white/60"
            }`}
          >
            <Icon size={14} />
            {l}
          </button>
        ))}
      </div>

      <main className="flex-1 px-6 lg:px-12 py-10 pb-24 lg:pb-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-2">Control Room</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl">{TABS.find((t) => t.k === tab)?.l}</h1>
          </div>
          <button onClick={load} data-testid="admin-refresh" className="text-xs tracking-[0.22em] uppercase text-white/60 hover:text-white">
            ↻ Refresh
          </button>
        </div>

        {tab === "overview" && stats && (
          <div data-testid="admin-overview" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { l: "Total Raised", v: `$${stats.total_raised.toLocaleString()}` },
              { l: "Donations", v: stats.total_donations },
              { l: "Pending Grants", v: stats.pending_grants },
              { l: "Approved Grants", v: stats.approved_grants },
              { l: "Total Grants", v: stats.total_grants },
              { l: "Volunteers", v: stats.total_volunteers },
              { l: "Messages", v: stats.total_contacts },
            ].map((s, i) => (
              <div key={i} className="border border-white/10 bg-[#121215] p-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">{s.l}</div>
                <div className="mt-3 font-serif-display text-4xl text-[#E85D04]">{s.v}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "donations" && <DataTable rows={data.donations} cols={["created_at","reference","name","email","amount","frequency","cause"]} testid="donations" />}
        {tab === "grants" && <GrantsTable rows={data.grants} onUpdate={load} />}
        {tab === "volunteers" && <DataTable rows={data.volunteers} cols={["created_at","name","email","city","availability","skills"]} testid="volunteers" />}
        {tab === "contacts" && <DataTable rows={data.contacts} cols={["created_at","name","email","subject","message"]} testid="contacts" />}
        {tab === "blog" && <BlogManager posts={data.blog} reload={load} />}
      </main>
    </div>
  );
}

function fmt(v) {
  if (v == null) return "—";
  if (typeof v === "string" && v.includes("T") && v.includes("-")) {
    try { return new Date(v).toLocaleString(); } catch (e) { return v; }
  }
  return String(v);
}

function DataTable({ rows, cols, testid }) {
  return (
    <div data-testid={`admin-table-${testid}`} className="border border-white/10 bg-[#121215] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0A0C] border-b border-white/10">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-white/40">{c.replace("_", " ")}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-white/40">No records yet</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
              {cols.map((c) => (
                <td key={c} className="px-4 py-3 text-white/80 align-top max-w-xs truncate">{fmt(r[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GrantsTable({ rows, onUpdate }) {
  const change = async (id, status) => {
    try {
      await api.patch(`/admin/grants/${id}`, { status });
      toast.success(`Grant ${status}`);
      onUpdate();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="admin-table-grants" className="border border-white/10 bg-[#121215] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0A0C] border-b border-white/10">
          <tr>
            {["Ref","Applicant","Project","Country","Amount","Status","Actions"].map((c) => (
              <th key={c} className="text-left px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-white/40">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40">No grant applications yet</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
              <td className="px-4 py-3 text-[#E85D04]">{r.reference}</td>
              <td className="px-4 py-3">{r.applicant_name}<div className="text-white/40 text-xs">{r.email}</div></td>
              <td className="px-4 py-3 max-w-xs">{r.project_title}<div className="text-white/40 text-xs truncate">{r.project_description}</div></td>
              <td className="px-4 py-3">{r.country}</td>
              <td className="px-4 py-3 text-white">${Number(r.amount_requested).toLocaleString()}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 text-[10px] tracking-[0.2em] uppercase ${
                  r.status === "approved" ? "bg-green-500/15 text-green-400" :
                  r.status === "rejected" ? "bg-red-500/15 text-red-400" :
                  r.status === "under-review" ? "bg-blue-500/15 text-blue-400" :
                  "bg-white/10 text-white/60"
                }`}>{r.status}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <button onClick={() => change(r.id, "approved")} data-testid={`grant-approve-${r.id}`} className="text-[10px] tracking-[0.2em] uppercase text-green-400 hover:text-green-300">Approve</button>
                  <button onClick={() => change(r.id, "rejected")} data-testid={`grant-reject-${r.id}`} className="text-[10px] tracking-[0.2em] uppercase text-red-400 hover:text-red-300">Reject</button>
                  <button onClick={() => change(r.id, "under-review")} className="text-[10px] tracking-[0.2em] uppercase text-blue-400 hover:text-blue-300">Review</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BlogManager({ posts, reload }) {
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", image_url: "", author: "John Depp Foundation" });
  const [creating, setCreating] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/blog", form);
      setForm({ title: "", excerpt: "", body: "", image_url: "", author: "John Depp Foundation" });
      toast.success("Story published");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div data-testid="admin-blog-manager" className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <form onSubmit={create} className="border border-white/10 bg-[#121215] p-6 space-y-4">
        <h3 className="font-serif-display text-2xl">New Story</h3>
        <input data-testid="blog-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required placeholder="Title" className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04]" />
        <input data-testid="blog-excerpt" value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} required placeholder="Excerpt" className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04]" />
        <textarea data-testid="blog-body" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} required rows={5} placeholder="Body" className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04] resize-none" />
        <input data-testid="blog-image" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="Image URL" className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04]" />
        <input value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} placeholder="Author" className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04]" />
        <button data-testid="blog-create-submit" type="submit" disabled={creating} className="bg-[#E85D04] hover:bg-[#DC2F02] px-6 py-3 text-xs tracking-[0.22em] uppercase disabled:opacity-60">
          {creating ? "Publishing…" : "Publish Story"}
        </button>
      </form>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="border border-white/10 bg-[#121215] p-4 flex items-center gap-4">
            {p.image_url && <div className="w-20 h-20 bg-cover bg-center shrink-0" style={{ backgroundImage: `url(${p.image_url})` }} />}
            <div className="flex-1 min-w-0">
              <div className="font-serif-display text-lg truncate">{p.title}</div>
              <div className="text-white/40 text-xs truncate">{p.excerpt}</div>
            </div>
            <button onClick={() => remove(p.id)} className="text-[10px] tracking-[0.22em] uppercase text-red-400 hover:text-red-300">Delete</button>
          </div>
        ))}
        {posts.length === 0 && <div className="text-white/40 text-sm">No stories yet.</div>}
      </div>
    </div>
  );
}
